import { Component, OnInit, TemplateRef, viewChild, inject } from '@angular/core';
import { FormService } from '../../services/form.service';
import { Form } from '../../data/form';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BrowserMessage,
  BrowserMessagesService,
  BrowserMessageType,
  ModalRef,
  ModalService,
  UserService,
  LoaderComponent
} from '@rbb/ng-web-template-lib';
import { ApproverDataToJson } from '../../data-to-json/approver-data-to-json';
import { ApproverFormData } from '../../custom-field-parameter/approver-form-data';
import { ApproverType } from '../../types/approver-type';
import { ActionFormData } from '../../custom-field-parameter/action-form-data';
import { cloneDeep } from 'lodash';
import { BodyForm } from '../../body/body-form';
import { NgClass, DatePipe } from '@angular/common';
import { FormDiffComponent } from '../form-diff/form-diff.component';
import { FormComponent } from '../form/form.component';

@Component({
  selector: 'app-editor-form-history',
  templateUrl: './editor-form-history.component.html',
  styleUrl: './editor-form-history.component.css',
  imports: [NgClass, FormDiffComponent, FormComponent, DatePipe, LoaderComponent]
})
export class EditorFormHistoryComponent implements OnInit {
  private formService = inject(FormService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private browserMessagesService = inject(BrowserMessagesService);
  private userService = inject(UserService);
  private modalService = inject(ModalService);

  id: string | undefined;
  forms: Form[] | undefined;
  selectedForm: number | undefined;

  readonly ngModalCopy = viewChild<TemplateRef<unknown>>('ngModalCopy');
  ngModalCopyService: ModalRef<unknown> | undefined;

  ngOnInit(): void {
    this.id = this.route.snapshot.params['id'];
    if (this.id) {
      this.formService.getFormHistory(this.id).subscribe({
        next: (forms: Form[]) => {
          for (const form of forms) {
            form.actionDataCustomParameter = new ActionFormData(form.actionData);

            const tmpApproverData: ApproverFormData[] = form.approverData ? JSON.parse(form.approverData) : [];
            form.approverDataCustomParameter = new Array<ApproverDataToJson>(tmpApproverData.length);
            for (let i = 0; i < tmpApproverData.length; i++) {
              const data = tmpApproverData[i];
              if (!data) {
                form.approverDataCustomParameter[i] = new ApproverDataToJson();
              } else {
                const tmpUser = new ApproverDataToJson();
                form.approverDataCustomParameter[i] = tmpUser;
                tmpUser.limit = data.limit ?? null;
                tmpUser.forInformationOnly = data.forInformationOnly;

                // GroupMember
                const groupMemberUsernames = [];
                if (data.groupMember && form.approverTypes?.[i] === ApproverType.APPROVER_GROUP) {
                  tmpUser.mailNotification = data.mailNotification;

                  for (const member of data.groupMember) {
                    if (member.username != null) {
                      groupMemberUsernames.push(member.username);
                    }
                  }
                }

                if (groupMemberUsernames) {
                  this.userService.getAdUsers(groupMemberUsernames).subscribe({
                    next: (approver) => {
                      tmpUser.groupMembers = approver;
                    },
                    error: (error) => {
                      this.browserMessagesService.showHttpError(error, 'Daten zu den Genehmigern konnten nicht geladen werden');
                    }
                  });
                }

                // Genehmiger
                const username = tmpApproverData[i].username;
                if (username) {
                  this.userService.getAdUsers([username]).subscribe({
                    next: (approver) => {
                      tmpUser.users = approver;
                    },
                    error: (error) => {
                      this.browserMessagesService.showHttpError(error, 'Daten zu den Genehmigern konnten nicht geladen werden');
                    }
                  });
                }

                // Deputy
                const deputyUsername = tmpApproverData[i].deputyUsername;
                if (deputyUsername) {
                  this.userService.getAdUsers([deputyUsername]).subscribe({
                    next: (approver) => {
                      tmpUser.deputyUsers = approver;
                    },
                    error: (error) => {
                      this.browserMessagesService.showHttpError(error, 'Daten zu den Genehmigern konnten nicht geladen werden');
                    }
                  });
                }
              }
            }
            form.dataFieldsCustomParameter = form.dataFields ? JSON.parse(form.dataFields) : [];
            if (form.fields) {
              for (let i = 0; i < form.fields.length; i++) {
                form.fields[i].fieldCustomParameter = JSON.parse(form.fields[i].definition ?? '{}');
                form.fields[i].order = i + 1;
              }
            }
          }
          this.forms = forms;
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Formularverlauf konnte nicht geladen werden');
        }
      });
    } else {
      this.router.navigateByUrl('/editor').then();
    }
  }

  openCopyForm(): void {
    const ngModalCopy = this.ngModalCopy();
    if (ngModalCopy) {
      this.ngModalCopyService = this.modalService.open(ngModalCopy, { centered: true, backdrop: 'static', size: 'xl' });
    }
  }

  closeCopyForm(): void {
    this.ngModalCopyService?.close();
  }

  copyForm(): void {
    if (this.selectedForm && this.forms && this.id) {
      const form: Form = cloneDeep(this.forms[0]);
      form.fields = cloneDeep(this.forms[this.selectedForm].fields);

      this.formService.updateForm(new BodyForm(form), this.id).subscribe({
        next: () => {
          this.closeCopyForm();
          this.browserMessagesService.addMessage(new BrowserMessage(
            'Gespeichert',
            'Das Formular wurde erfolgreich kopiert.',
            BrowserMessageType.Success
          ));
          this.router.navigateByUrl('/editor').then();
        },
        error: (error) => {
          this.closeCopyForm();
          this.browserMessagesService.showHttpError(error, 'Das Formular konnte nicht kopiert werden.');
        }
      });
    }
  }
}
